import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { saveEmotion, getEmotions, EmotionRecord } from '../services/emotionService';
import { getColor } from '../utils/colorUtils';

const MindTempWidget: React.FC = () => {
  const [value, setValue] = useState<number>(38);
  const [history, setHistory] = useState<EmotionRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmittedToday, setIsSubmittedToday] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const data = await getEmotions();
      const sortedData = _.orderBy(data, ['createdAt'], ['desc']);
      setHistory(sortedData);
      if (sortedData.length > 0) {
        const lastRecordDate = new Date(sortedData[0].createdAt).toDateString();
        const todayDate = new Date().toDateString();
        if (lastRecordDate === todayDate) {
          setIsSubmittedToday(true);
          setValue(sortedData[0].value);
        }
      }
    } catch (error) {
      console.error('Failed to fetch emotion history:', error);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await saveEmotion(value);
      fetchHistory();
    } catch (error) {
      console.error('Failed to save emotion:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTempDescription = (val: number) => {
    // 38도 스케일 및 메시지 논리 수정
    if (val < 8) return '스스로를 돌보는 시간이 필요해요. 무엇이 당신을 힘들게 하나요?';
    if (val <= 15) return '마음이 조금 흐린 날이네요. 작은 위로가 큰 힘이 될 수 있어요.';
    if (val <= 23) return '마음이 고요하지만 살짝 무겁군요. 따뜻한 차 한 잔이 어울리는 날이에요.';
    if (val <= 30) return '오늘의 작은 행복들이 빛나는군요. 주변에 그 빛을 나눠주세요.';
    return '최고의 하루네요! 이 에너지를 내일에도 이어가 보세요.';
  };

  const getTempStatusInfo = (val: number) => {
    // 38도 스케일로 재조정된 상태 정보
    if (val < 8) return { status: '위험', color: '#002ac2ff' };
    if (val <= 15) return { status: '주의', color: '#5F7ADB' };
    if (val <= 23) return { status: '보통', color: '#7AC4B3' };
    if (val <= 30) return { status: '좋음', color: '#F8D45B' };
    return { status: '아주 좋음', color: '#E65C5C' };
  };

  const tempStatusInfo = getTempStatusInfo(value);

  return (
    <div className="widget-card">
      <div className="thermometer-display">
        <div
          className="status-badge"
          style={{ borderColor: tempStatusInfo.color, color: tempStatusInfo.color }}
        >
          {tempStatusInfo.status}
        </div>
        <span className="value-text" style={{ color: tempStatusInfo.color }}>
          {value}°
        </span>
      </div>

      <p className="widget-card__description" style={{ color: tempStatusInfo.color }}>
        {getTempDescription(value)}
      </p>

      <div className="temp-top-labels">
        <span>차가움</span>
        <span>따뜻함</span>
      </div>

      <div className="slider-container">
        <input
          type="range"
          min="0"
          max="38"
          value={value}
          onChange={e => setValue(Number(e.target.value))}
          className="slider"
          style={{ '--slider-color': tempStatusInfo.color } as React.CSSProperties}
          disabled={isLoading || isSubmittedToday}
        />
        <div className="temp-labels">
          <span>0°</span>
          <span>10°</span>
          <span>20°</span>
          <span>30°</span>
          <span>38°</span>
        </div>
      </div>

      <button
        onClick={handleSave}
        className="widget-card__button"
        disabled={isLoading || isSubmittedToday}
      >
        {isSubmittedToday ? '기록 완료' : isLoading ? '저장 중...' : '기록하기'}
      </button>

      <div className="history-log">
        <h4>최근 기록</h4>
        <ul>
          {_.take(history, 4).map((record: any) => {
            const recordStatus = getTempStatusInfo(record.value);
            return (
              <li key={record._id}>
                <div className="history-log__item-info">
                  <span
                    className="history-log__badge"
                    style={{
                      border: '2px solid',
                      borderColor: recordStatus.color,
                      color: recordStatus.color,
                    }}
                  >
                    {recordStatus.status}
                  </span>
                  <span className="history-log__value">{record.value}°</span>
                </div>
                <span className="history-log__date">
                  {new Date(record.createdAt)
                    .toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })
                    .replace(/\. /g, '.')
                    .slice(0, -1)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default MindTempWidget;
